import React, { useState, useCallback, useEffect } from 'react'
import { Box } from '@hub/box'
import { Cluster, ClusterItem } from '@hub/cluster'
import { CoreContainer } from '@hub/core-container'
import { Stack } from '@hub/stack'
import { Tabs } from '@hub/tabs'
import Account from '../../components/universal-header/nav/account'
import type { IUniversalCart } from '../../types'
import type { User } from '../../../types/user'
import { getAlgoliaApiKeys } from '../../config'
import Search from '../../components/search'
import analyticsUtils from '../../helpers/analytics-utils'
import trackEvent from '../../helpers/analytics-utils/track-event'
import { HamburgerButton } from './hamburger-button'
import { HamburgerMenu } from './hamburger-menu'
import type { InitialPathType, OpenHamburgerMenuAtPathType } from './types'
import { UniversalHeaderLogos } from './universal-header-logos'
import { SearchButton } from './search-button'
import { AccountPane } from './account-pane'
import { UniversalHeaderTabs } from './tabs'
import { Cart } from './cart'
import { HeaderTabs } from './tabs/header-tabs'

interface UniversalHeaderProps {
  logo: React.ComponentProps<typeof UniversalHeaderLogos>['logo']
  hasBottomMargin?: boolean
  hamburgerMenuProps: React.ComponentProps<typeof HamburgerMenu>
  searchProps?: React.ComponentProps<typeof SearchButton>
  headerTabsProps: React.ComponentProps<typeof UniversalHeaderTabs>
  cart: IUniversalCart
  navPath?: InitialPathType
  user: User
  logoUrl?: string
}
export * from './use-universal-header'
export * from './use-universal-header/fetch-universal-header-static-data'

export const toggleScroll = (isMenuOpen: boolean): void => {
  if (isMenuOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.removeProperty('overflow')
  }
}

export const UniversalHeader: React.FC<UniversalHeaderProps> = ({
  logo,
  hasBottomMargin = true,
  hamburgerMenuProps,
  searchProps,
  headerTabsProps,
  cart,
  navPath,
  user,
  logoUrl,
}) => {
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false)
  const [hamburgerMenuPath, setHamburgerMenuPath] = useState<
    undefined | InitialPathType
  >(navPath)

  const openHamburgerMenuAtPath = useCallback<OpenHamburgerMenuAtPathType>(
    path => {
      toggleScroll(true)
      setIsHamburgerMenuOpen(true)
      setHamburgerMenuPath(path ?? [])
    },
    []
  )
  const closeNav = useCallback(() => {
    toggleScroll(false)
    setIsHamburgerMenuOpen(false)
  }, [])
  const [isAccountPaneOpen, setIsAccount] = useState(false)
  const displayAccountNav = useCallback((displayNav: boolean) => {
    toggleScroll(displayNav)
    setIsAccount(displayNav)
    trackEvent(
      analyticsUtils.categories.UNIVERSAL_HEADER,
      displayNav
        ? analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_OPEN
        : analyticsUtils.actions.UNIVERSAL_HEADER_NAV_MENU_CLOSE,
      analyticsUtils.labels.ACCOUNT_ICON
    )
  }, [])

  useEffect(() => {
    if (navPath) {
      openHamburgerMenuAtPath(navPath)
    }
  }, [navPath, openHamburgerMenuAtPath])

  // The 'order' below is not a magic number, it's an ordinal, and I can't tell
  // the eslint rule to respect ordinals, so I have to turn it off.
  /* eslint-disable no-magic-numbers */
  return (
    <Tabs
      variant="unstyled"
      {...(hasBottomMargin && {
        sx: { marginBottom: ['spacing-md', null, 'spacing-lg'] },
      })}
    >
      <Stack shouldWrapChildren={false}>
        <CoreContainer
          sx={{
            minHeight: 'size-9',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Cluster align="flex-end" gapX="spacing-md" sx={{ flexGrow: 1 }}>
            <HamburgerButton
              expanded={isHamburgerMenuOpen}
              menuId="universal-header-hamburger-menu"
              sx={{
                alignSelf: 'flex-end',
                order: [6, 6, 1],
                marginBottom: ['spacing-sm', 0],
                marginTop: ['spacing-xs', 0],
              }}
              onToggle={() => openHamburgerMenuAtPath()}
            />
            <Box
              sx={{
                alignSelf: 'flex-end',
                order: [1, 1, 2],
                marginBottom: ['spacing-md', 'spacing-sm'],
                marginTop: ['spacing-xs', null],
              }}
            >
              <UniversalHeaderLogos
                logo={logo}
                boxSize="size-icon-line-height-base"
                url={logoUrl}
                sx={{ paddingBottom: ['spacing-md', 0] }}
              />
            </Box>

            <ClusterItem
              sx={{
                alignSelf: 'flex-end',
                order: [7, 7, 3],
              }}
              width={['100%', null, 'auto']}
            >
              <HeaderTabs
                tabsProps={headerTabsProps}
                sx={{
                  marginLeft: ['-24px', null, 0],
                  marginRight: ['-24px', null, 0],
                }}
              />
            </ClusterItem>
            <Box sx={{ flexGrow: 1, order: [2, 2, 4] }} />
            <Search
              country="Australia"
              centre={headerTabsProps.centre}
              algoliaApiKeys={getAlgoliaApiKeys()}
            >
              {showSearch => (
                <SearchButton
                  {...searchProps}
                  sx={{
                    alignSelf: 'flex-end',
                    marginBottom: ['spacing-md', 'spacing-sm'],
                    marginTop: ['spacing-xs', null],
                    order: [3, 3, 5],
                    position: 'relative',
                    top: [null, null, '-4px'],
                  }}
                  controlledElementId="universal-search-modal"
                  onClick={showSearch}
                />
              )}
            </Search>
            <AccountPane
              isLoggedIn={Boolean(user)}
              name={user?.firstName}
              displayAccountNav={displayAccountNav}
              sx={{
                alignSelf: 'flex-end',
                marginBottom: ['spacing-md', 'spacing-sm'],
                marginTop: ['spacing-xs', null],
                order: [4, 4, 6],
              }}
            />
            <Cart
              sx={{
                order: [5, 5, 6],
                alignSelf: 'flex-end',
                marginBottom: ['spacing-md', 'spacing-sm'],
                marginTop: ['spacing-xs', null],
              }}
              count={cart?.count || 0}
              url={cart?.url}
            />
          </Cluster>
        </CoreContainer>
        <UniversalHeaderTabs
          {...headerTabsProps}
          openHamburgerMenuAtPath={openHamburgerMenuAtPath}
        />
      </Stack>
      {isHamburgerMenuOpen && (
        <HamburgerMenu
          {...hamburgerMenuProps}
          menuId="universal-header-hamburger-menu"
          initialPath={hamburgerMenuPath}
          onClose={() => {
            closeNav()
            hamburgerMenuProps.onClose?.()
          }}
        />
      )}
      {isAccountPaneOpen && (
        <Account user={user} onClose={() => displayAccountNav(false)} />
      )}
    </Tabs>
  )
  /* eslint-enable no-magic-numbers */
}
